import React, { useEffect, useRef, useState } from 'react';

interface Props {
  // Accepts the display string as authored: "3,200+", "2,900+", "100", "12+", "5.0".
  // Anything non-numeric (e.g. "Certified") is rendered as-is, un-animated.
  value: string;
  durationMs?: number;
  className?: string;
  style?: React.CSSProperties;
}

interface Parsed {
  prefix: string;
  target: number;
  suffix: string;
  decimals: number;
  grouped: boolean;
}

// Pull the first number out of the display string and keep whatever wraps it,
// so the animation never changes the authored copy — only how it arrives.
function parse(value: string): Parsed | null {
  const m = value.match(/^(\D*?)([\d,]+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  const [, prefix, numRaw, suffix] = m;
  const grouped = numRaw.includes(',');
  const plain = numRaw.replace(/,/g, '');
  const target = parseFloat(plain);
  if (!isFinite(target)) return null;
  const dot = plain.indexOf('.');
  return { prefix, target, suffix, decimals: dot === -1 ? 0 : plain.length - dot - 1, grouped };
}

function format(n: number, p: Parsed): string {
  const fixed = n.toFixed(p.decimals);
  if (!p.grouped) return fixed;
  const [int, frac] = fixed.split('.');
  const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${withSep}.${frac}` : withSep;
}

// easeOutExpo — fast start, long settle. Reads as deliberate rather than mechanical.
const ease = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

const CountUp: React.FC<Props> = ({ value, durationMs = 1600, className, style }) => {
  const parsed = parse(value);
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(() => (parsed ? format(0, parsed) : value));
  const doneRef = useRef(false);

  useEffect(() => {
    if (!parsed) return;
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDisplay(format(parsed.target, parsed));
      doneRef.current = true;
      return;
    }

    let raf = 0;
    let start = 0;

    const run = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / durationMs);
      setDisplay(format(parsed.target * ease(t), parsed));
      if (t < 1) raf = requestAnimationFrame(run);
    };

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !doneRef.current) {
          doneRef.current = true;
          raf = requestAnimationFrame(run);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });

    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
    // parsed is derived from `value`; depending on value keeps this stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs]);

  if (!parsed) return <span className={className} style={style}>{value}</span>;

  return (
    <span ref={ref} className={className} style={style}>
      {parsed.prefix}
      {/* tabular figures stop the number jittering as digits change width */}
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{display}</span>
      {parsed.suffix}
    </span>
  );
};

export default CountUp;
